import React, { useMemo } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { formatDurationText } from '../../../utils/calculatorUtils';
import { formatCurrency } from '../calculatorHelpers';
import { calculateCosts } from '../costCalculation';
import type { CalculatorFormState, SubmissionStatus } from '../types';
import EmailForm from './EmailForm';
import styles from '../Calculator.module.css';

interface StepEstimateProps {
    formData: CalculatorFormState;
    submissionStatus: SubmissionStatus;
    onEmailChange: (value: string) => void;
    onTermsToggle: () => void;
    onSubmit: () => void;
}

const getTranslatedTeamTitle = (role: string, t: (key: string) => string): string => {
    if (role === 'developer2') return t('calculator.team.developer');
    return t(`calculator.team.${role}`);
};

const StepEstimate: React.FC<StepEstimateProps> = ({
    formData,
    submissionStatus,
    onEmailChange,
    onTermsToggle,
    onSubmit,
}) => {
    const { t, language } = useTranslation();
    const costs = useMemo(() => calculateCosts(formData), [formData]);

    const teamEntries = Object.entries(formData.teamComposition).filter(
        ([key, quantity]) => key !== 'developer2' && quantity > 0
    );

    return (
        <div className={styles.columnContainer}>
            <div className={styles.header}>
                <h2 className={styles.resultTitle}>{t('calculator.estimate.title')}</h2>
                <p className={styles.subtitle}>{t('calculator.estimate.subtitle')}</p>
            </div>
            <div className={styles.lastStep}>
                <div className={styles.resultsContainer}>
                    <div className={styles.resultsCard}>
                        <h3 className={styles.sectionTitle}>
                            {t('calculator.estimate.exampleTitle')}
                        </h3>
                        <div className={styles.costBreakdown}>
                            <div className={styles.costItem}>
                                <span>{t('calculator.estimate.workshop')}</span>
                                <span className={styles.value}>
                                    {formatCurrency(costs.designCost, costs.currency)} {costs.currency}
                                </span>
                            </div>
                            <div className={styles.costItem}>
                                <span>{t('calculator.estimate.tranches')}</span>
                                <span className={styles.value}>
                                    {costs.trancheCount}x{' '}
                                    {formatCurrency(costs.costPerTranche, costs.currency)}{' '}
                                    {costs.currency}
                                </span>
                            </div>
                            <div className={styles.costTotal}>
                                <span>{t('calculator.estimate.total')}</span>
                                <span className={styles.value}>
                                    {formatCurrency(costs.total, costs.currency)} {costs.currency}
                                </span>
                            </div>
                            <div className={styles.vatNote}>{t('calculator.estimate.vat')}</div>
                        </div>
                        <div className={styles.projectDetails}>
                            <div className={styles.teamSection}>
                                <h3 className={styles.sectionTitle}>
                                    {t('calculator.estimate.teamMembers')}
                                </h3>
                                <div className={styles.teamList}>
                                    {teamEntries.map(([key, quantity]) => {
                                        let displayQuantity = quantity;
                                        if (
                                            key === 'developer' &&
                                            'developer2' in formData.teamComposition
                                        ) {
                                            displayQuantity +=
                                                formData.teamComposition['developer2'] || 0;
                                        }
                                        return (
                                            <div key={key} className={styles.teamMember}>
                                                <span>{getTranslatedTeamTitle(key, t)}</span>
                                                <span className={styles.value}>
                                                    x{displayQuantity}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className={styles.assumptions}>
                                <h3 className={styles.sectionTitle}>
                                    {t('calculator.estimate.assumptions')}
                                </h3>
                                <div className={styles.assumptionItems}>
                                    <div className={styles.assumptionItem}>
                                        <span>{t('calculator.estimate.development')}</span>
                                        <span className={styles.value}>
                                            {formatDurationText(formData.projectDuration, language)}
                                        </span>
                                    </div>
                                    <div className={styles.assumptionItem}>
                                        <span>{t('calculator.estimate.milestones')}</span>
                                        <span className={styles.value}>{costs.milestones}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <EmailForm
                    email={formData.email}
                    acceptTerms={formData.acceptTerms}
                    submissionStatus={submissionStatus}
                    onEmailChange={onEmailChange}
                    onTermsToggle={onTermsToggle}
                    onSubmit={onSubmit}
                />
            </div>
        </div>
    );
};

export default StepEstimate;
